import { useEffect, useRef } from 'react'
import { Loader2, MessageCircle, SendHorizontal } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useJobChat } from '@/hooks/use-job-chat'
import { cn } from '@/lib/utils'
import type { Job } from '@/types'

type JobChatSheetProps = {
  job: Job | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

function formatMessageTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function JobChatSheet({ job, open, onOpenChange }: JobChatSheetProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const {
    messages,
    loading,
    sending,
    error,
    draft,
    setDraft,
    sendMessage,
    isOwnMessage,
    reload,
  } = useJobChat({
    jobId: job?.id ?? null,
    open,
  })

  useEffect(() => {
    const container = scrollRef.current
    if (!container) {
      return
    }
    container.scrollTop = container.scrollHeight
  }, [messages, open])

  const handleSend = () => {
    void sendMessage()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-b border-border/70 px-4 py-4 text-left">
          <div className="flex items-start gap-3 pr-8">
            <div className="rounded-full bg-primary/10 p-2 text-primary">
              <MessageCircle className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <SheetTitle className="line-clamp-2 text-base">
                {job?.title ?? 'Job chat'}
              </SheetTitle>
              <SheetDescription className="mt-1">
                {job?.job_ref ? `${job.job_ref} · ` : ''}
                Chat with workers and team on this job
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {loading && messages.length === 0 ? (
            <div className="flex min-h-40 items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading conversation...
            </div>
          ) : null}

          {!loading && messages.length === 0 ? (
            <div className="flex min-h-40 flex-col items-center justify-center rounded-xl border border-dashed border-border/70 bg-muted/20 px-4 text-center">
              <MessageCircle className="mb-2 h-8 w-8 text-muted-foreground/70" />
              <p className="text-sm font-medium text-foreground">No messages yet</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Start the conversation with your assigned worker.
              </p>
            </div>
          ) : null}

          {messages.map((message) => {
            const own = isOwnMessage(message)
            const isSystem = message.message_type === 'system' || Boolean(message.system_event)

            if (isSystem) {
              return (
                <div key={message.id} className="flex justify-center">
                  <span className="rounded-full bg-muted px-3 py-1 text-[11px] text-muted-foreground">
                    {message.body || message.system_event}
                  </span>
                </div>
              )
            }

            return (
              <div
                key={message.id}
                className={cn('flex', own ? 'justify-end' : 'justify-start')}
              >
                <div
                  className={cn(
                    'max-w-[85%] rounded-2xl px-3 py-2 text-sm shadow-sm',
                    own
                      ? 'rounded-br-md bg-primary text-primary-foreground'
                      : 'rounded-bl-md border border-border/70 bg-card text-foreground',
                  )}
                >
                  <p className="whitespace-pre-wrap break-words">{message.body}</p>
                  <p
                    className={cn(
                      'mt-1 text-[10px]',
                      own ? 'text-primary-foreground/75' : 'text-muted-foreground',
                    )}
                  >
                    {formatMessageTime(message.created_at)}
                  </p>
                </div>
              </div>
            )
          })}
        </div>

        <div className="border-t border-border/70 bg-background p-4">
          {error ? (
            <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {error}
              <button
                type="button"
                className="ml-2 font-medium underline"
                onClick={() => {
                  void reload()
                }}
              >
                Retry
              </button>
            </div>
          ) : null}

          <div className="flex items-end gap-2">
            <Textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Write a message..."
              rows={2}
              className="min-h-[72px] resize-none"
              disabled={sending}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  handleSend()
                }
              }}
            />
            <Button
              type="button"
              size="icon"
              className="h-10 w-10 shrink-0"
              disabled={sending || !draft.trim()}
              onClick={handleSend}
              aria-label="Send message"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" />}
            </Button>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Press Enter to send, Shift+Enter for a new line.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  )
}
